import React from 'react';
import ConfirmDialog from '../ConfirmDialog';
import { DELETE_STUDENTS_TITLE, DELETE_STUDENTS_MESSAGE } from '../../domain/people';
import { deleteProgressLine } from '../../domain/roster';

// The confirm behind `Delete Students` on the students list's bulk Actions menu
// (#175).
//
// The copy is MMS's, verbatim, and it does the work: it is the only place a
// teacher is told that this button usually does not delete anybody — a
// student with lesson or billing history is switched to Inactive and kept. The
// rule is `planStudentDeletion` in domain/people.js, which exports the strings
// so this component and its test cannot drift from it.
//
// No count and no preview, as in MMS: no list of attached lessons or invoices,
// no mention of the family or its balance, no offer to reassign. The server
// queries that would give a count (#318) run after this dialog is confirmed,
// so a count here would ask the whole selection twice.
//
// ── Progress, which MMS does not show (#331) ───────────────────────────────
//
// `progress` renders `Checked 12 of 40.` while the delete runs. MMS's delete is
// one server call; ours asks the server before it writes (#318) — up to seven
// `limit(1)` queries per selected student, eight at a time — so a large
// selection would otherwise be a disabled button and a still dialog for as
// long as the network takes. It is progress, not a preview: work already done,
// which the loop knows exactly, never a prediction of who will be deleted. It
// is absent unless a caller passes it. The count moves in jumps of up to
// eight, the probe concurrency (`probeSelection` in data/useStudents.jsx).
export default function DeleteStudentsDialog({
  open, busy = false, progress = null, onCancel, onConfirm,
}) {
  // Under MMS's sentence rather than in place of it: the sentence is the only
  // place a teacher is told this button usually keeps people.
  const message = progress && progress.total
    ? (
      <>
        {DELETE_STUDENTS_MESSAGE}
        <div aria-live="polite">{deleteProgressLine(progress)}</div>
      </>
    )
    : DELETE_STUDENTS_MESSAGE;

  return (
    <ConfirmDialog
      open={open}
      title={DELETE_STUDENTS_TITLE}
      message={message}
      confirmLabel="Delete"
      confirmDisabled={busy}
      // `Cancel` then `Delete`, MMS's pair and order. An empty `cancelLabel`
      // gives `ConfirmDialog` two buttons rather than its three-way shape.
      discardLabel="Cancel"
      cancelLabel=""
      onDiscard={onCancel}
      onCancel={onCancel}
      onConfirm={onConfirm}
    />
  );
}
